import {
   CurrentHealthcareProviderConfiguration,
   CurrentHealthcareProviderConfigurationQuery,
   CurrentHealthcareProviderPatientPortalBinding,
   CurrentHealthcareProviderPatientPortalBindingQuery,
} from '@dreryk/egabinet-patient-portal-frontend-api';
import {
   CurrentHealthcareOrganizationQuery,
   CurrentHealthcarePracticeQuery,
} from '@dreryk/egabinet-healthcare-provider-frontend-api';
import { EmailAddress } from '@dreryk/email-address-common-api';

import { useCqrsFind, useCqrsGet, UseCqrsQueryResult } from '@dreryk/command-query-react-common';
import { LoaderSpinner, Message, MessageType } from '@dreryk/react-components';

import { useResourceBranchByHealthcareProviderType } from '~libs.shared.account';

import { patientPortalTranslationKeys } from '../translations';

const useCurrentHealthcareProviderPatientPortalBindingQuery = (): UseCqrsQueryResult<CurrentHealthcareProviderPatientPortalBinding> => {
   return useCqrsGet(() => {
      return new CurrentHealthcareProviderPatientPortalBindingQuery();
   });
};

type HealthcareProviderConfigurationPartForPatientPortal = {
   emailAddress: EmailAddress | null;
};

type HealthcareProviderPatientPortalBindingLoaderRendererProps = {
   healthcareProviderPatientPortalBinding: CurrentHealthcareProviderPatientPortalBinding;
   healthcareProviderConfiguration: CurrentHealthcareProviderConfiguration | null;
   healthcareProviderEmailAddress: EmailAddress | null;
};

type HealthcareProviderPatientPortalBindingLoaderProps = {
   readonly children: (result: HealthcareProviderPatientPortalBindingLoaderRendererProps) => JSX.Element;
};

export const HealthcareProviderPatientPortalBindingLoader = (
   props: HealthcareProviderPatientPortalBindingLoaderProps
): JSX.Element => {
   const HealthcareProviderDataQuery = useResourceBranchByHealthcareProviderType(
      CurrentHealthcareOrganizationQuery,
      CurrentHealthcarePracticeQuery
   );
   const {
      data: healthcareOrganization,
      loading: healthcareOrganizationLoading,
      error: healthcareOrganizationError,
   } = useCqrsGet<HealthcareProviderConfigurationPartForPatientPortal>(() => new HealthcareProviderDataQuery());

   const {
      data: healthcareProviderPatientPortalBinding,
      loading: healthcareProviderPatientPortalBindingLoading,
      error: healthcareProviderPatientPortalBindingError,
   } = useCurrentHealthcareProviderPatientPortalBindingQuery();

   const {
      data: healthcareProviderConfiguration,
      loading: healthcareProviderConfigurationLoading,
      error: healthcareProviderConfigurationError,
   } = useCqrsFind<CurrentHealthcareProviderConfiguration | null>(
      () => new CurrentHealthcareProviderConfigurationQuery()
   );

   if (
      healthcareProviderPatientPortalBindingLoading ||
      healthcareProviderConfigurationLoading ||
      healthcareOrganizationLoading
   ) {
      return <LoaderSpinner />;
   }

   if (
      healthcareProviderPatientPortalBindingError ||
      healthcareProviderConfigurationError ||
      healthcareOrganizationError
   ) {
      return (
         <div>
            <Message
               testId={'dataFetchingFaultStatus'}
               type={MessageType.NEGATIVE}
               label={patientPortalTranslationKeys.dataFetchingFault}
            />
         </div>
      );
   }

   return props.children({
      healthcareProviderPatientPortalBinding: healthcareProviderPatientPortalBinding,
      healthcareProviderConfiguration: healthcareProviderConfiguration,
      healthcareProviderEmailAddress: healthcareOrganization.emailAddress,
   });
};
